import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  computed,
  input,
  linkedSignal,
  output,
  signal,
  inject,
  viewChild,
} from '@angular/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { AnalyticsService } from '../../services/analytics.service';
import { CreateLabelMutationVariables, LabelService } from '../../services/label.service';
import { Label } from '../../types/label';
import { pickRandomLabelColor } from '../label-colors';
import { EVENT_ADDED_LABEL, EVENT_REMOVED_LABEL } from '../../analytics/events';
import { UiFieldComponent } from '../../ui/field/field.component';
import {
  HlmComboboxChip,
  HlmComboboxChipInput,
  HlmComboboxChips,
  HlmComboboxContent,
  HlmComboboxEmpty,
  HlmComboboxItem,
  HlmComboboxList,
  HlmComboboxMultiple,
  HlmComboboxPortal,
  HlmComboboxValues,
} from '@budgets/ui/helm/combobox';
import { LowerCasePipe } from '@angular/common';

interface CreateLabelOption {
  id: string;
  name: string;
  color: string;
  create: true;
}

type LabelComboboxOption = Label | CreateLabelOption;

@Component({
  selector: 'app-label-typeahead',
  templateUrl: './label-typeahead.component.html',
  imports: [
    UiFieldComponent,
    HlmComboboxMultiple,
    HlmComboboxChips,
    HlmComboboxValues,
    HlmComboboxChip,
    HlmComboboxChipInput,
    HlmComboboxPortal,
    HlmComboboxContent,
    HlmComboboxEmpty,
    HlmComboboxList,
    HlmComboboxItem,
    LowerCasePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LabelTypeaheadComponent {
  private analytics = inject(AnalyticsService);
  private labelsService = inject(LabelService);

  private static nextId = 0;

  readonly inputId = `label-typeahead-input-${LabelTypeaheadComponent.nextId}`;
  readonly listboxId = `label-typeahead-listbox-${LabelTypeaheadComponent.nextId++}`;

  readonly selectedLabelIds = input<string[] | null>([]);
  // Local selection: follows the input, and changes immediately when the user picks or removes a label.
  readonly selectedLabelIdsState = linkedSignal(() => this.selectedLabelIds() ?? []);
  readonly canCreate = input(true);
  readonly placeholder = input('Labels');
  readonly hideLabel = input(false, { transform: booleanAttribute });
  readonly keepOpenOnSelect = input(false);

  readonly selectedLabelIdsChange = output<string[]>();

  readonly labelInput = viewChild<ElementRef<HTMLInputElement>>('labelInput');

  readonly currentLabelText = signal('');
  readonly panelOpen = signal(false);
  readonly createLabelOption = computed(() => this.toCreateLabelOption(this.currentLabelText()));

  readonly labelsQuery = injectQuery(() => this.labelsService.labelListQueryOptions());
  readonly labels = computed(() => this.labelsQuery.data() ?? []);
  readonly filteredLabels = computed(() => {
    const lowerCaseSearch = this.currentLabelText().trim().toLowerCase();
    const selectedLabelIds = this.selectedLabelIdsState();
    return this.labels().filter(
      label =>
        (lowerCaseSearch === '' || label.name.toLowerCase().includes(lowerCaseSearch)) &&
        !selectedLabelIds.includes(label.id),
    );
  });
  readonly labelMap = computed(() =>
    this.labels().reduce<Record<string, Label>>((acc, curr) => {
      acc[curr.id] = curr;
      return acc;
    }, {}),
  );
  readonly selectedLabels = computed(() =>
    this.selectedLabelIdsState()
      .map(labelId => this.labelMap()[labelId])
      .filter((label): label is Label => !!label),
  );
  readonly canCreateLabel = computed(() => {
    const term = this.currentLabelText().trim().toLowerCase();
    return this.canCreate() === true && term !== '' && this.labels().every(label => label.name.toLowerCase() !== term);
  });
  readonly createLabelMutation = injectMutation(() => this.labelsService.createLabelMutationOptions());

  readonly labelOptionToString = (option: LabelComboboxOption | null): string => option?.name ?? '';

  readonly isLabelOptionEqual = (
    option: LabelComboboxOption | null,
    selectedOption: LabelComboboxOption | null,
  ): boolean => option?.id === selectedOption?.id;

  openPanel() {
    this.panelOpen.set(true);
  }

  closePanel() {
    this.panelOpen.set(false);
  }

  onPanelStateChange(state: 'open' | 'closed') {
    this.panelOpen.set(state === 'open');
    if (state === 'closed') this.clearInput();
  }

  onSearchChange(search: string) {
    this.currentLabelText.set(search);
    if (search) this.openPanel();
  }

  onSelectedOptionsChange(options: Array<LabelComboboxOption | null> | null) {
    const selectedOptions = (options ?? []).filter((option): option is LabelComboboxOption => !!option);
    const createOption = selectedOptions.find(option => this.isCreateLabelOption(option));

    if (createOption) {
      void this.createAndAddLabel(createOption.name);
      return;
    }

    this.setSelectedLabelIds(
      selectedOptions.filter((option): option is Label => !this.isCreateLabelOption(option)).map(label => label.id),
    );
    this.finalizeSelection();
  }

  inputChanged() {
    this.onSearchChange(this.currentLabelText());
    this.openPanel();
  }

  onInputKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.addCurrentLabel();
  }

  addCurrentLabel() {
    const currentLabelText = this.currentLabelText().trim();
    if (!currentLabelText) return;

    const lowerCaseSearch = currentLabelText.toLowerCase();
    const label = this.labels().find(existingLabel => existingLabel.name.trim().toLowerCase() === lowerCaseSearch);
    if (label) {
      this.addLabelId(label.id);
      this.finalizeSelection();
      return;
    }

    void this.createAndAddLabel(currentLabelText);
  }

  selectLabel(label: Label) {
    this.addLabelId(label.id);
    this.finalizeSelection();
  }

  private finalizeSelection() {
    this.clearInput();
    if (this.keepOpenOnSelect()) {
      this.openPanel();
      this.labelInput()?.nativeElement.focus();
      return;
    }

    this.closePanel();
  }

  private addLabelId(labelId: string): void {
    if (this.selectedLabelIdsState().includes(labelId)) return;

    this.setSelectedLabelIds([...this.selectedLabelIdsState(), labelId]);
  }

  remove(labelId: string): void {
    if (!labelId) return;

    this.setSelectedLabelIds(this.selectedLabelIdsState().filter(selectedId => selectedId !== labelId));
  }

  async createAndAddLabel(name: string): Promise<void> {
    const color = pickRandomLabelColor();
    const label = await this.createLabelMutation.mutateAsync({ name, color } satisfies CreateLabelMutationVariables);
    this.addLabelId(label.id);
    this.finalizeSelection();
  }

  private clearInput() {
    this.currentLabelText.set('');
    const labelInput = this.labelInput();
    if (labelInput) labelInput.nativeElement.value = '';
  }

  private setSelectedLabelIds(labelIds: string[]): void {
    const nextLabelIds = [...labelIds];
    const added = nextLabelIds.some(labelId => !this.selectedLabelIdsState().includes(labelId));
    const removed = this.selectedLabelIdsState().some(labelId => !nextLabelIds.includes(labelId));

    if (!added && !removed && nextLabelIds.length === this.selectedLabelIdsState().length) return;

    this.selectedLabelIdsState.set(nextLabelIds);
    this.selectedLabelIdsChange.emit(nextLabelIds);
    if (added) this.analytics.sendEvent(EVENT_ADDED_LABEL);
    if (removed) this.analytics.sendEvent(EVENT_REMOVED_LABEL);
  }

  private toCreateLabelOption(name: string): CreateLabelOption {
    return {
      id: `create:${name.trim().toLowerCase()}`,
      name: name.trim(),
      color: 'transparent',
      create: true,
    };
  }

  private isCreateLabelOption(option: LabelComboboxOption): option is CreateLabelOption {
    return 'create' in option && option.create === true;
  }
}
